import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { InputText } from 'primereact/inputtext';

import { useTranslation } from 'react-i18next';
import { Group } from '../../types/Group';

import { Toast } from 'primereact/toast';

import pinStatusService from '../../service/api/PinStatusService';
import { TOAST_LIFETIME } from '../../settings';
import { Button } from 'primereact/button';
import { hasPermission } from '../_shared/hasPermission';
import { useSelector } from 'react-redux';
import { PinStatusResponse } from '../../types/PinStatus';

export const PinManagement = () => {
  const toast = useRef<any>();

  const [globalFilter, setGlobalFilter] = useState('');

  const [pins, setPins] = useState<PinStatusResponse[]>([]);

  const { t } = useTranslation();

  const connectorTypeNameMap = {
    INPUT: t('groupTemplates.input'),
    OUTPUT: t('groupTemplates.output'),
  };

  const loggedUserContext = useSelector((state: any) => state.user.context);
  const canEditAll = hasPermission('IO_PIN_EDIT_ALL', loggedUserContext.permissions);

  const showToast = (summary: string, detail: string, severity: 'success' | 'info' | 'warn' | 'error') => {
    toast.current.show({
      severity: severity,
      summary: summary,
      detail: detail,
      life: TOAST_LIFETIME,
    });
  };

  const loadData = async () => {
    const statuses = await pinStatusService.getAll();
    setPins(statuses);
  };

  const [time, setTime] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setTime(Date.now()), 3000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  const groupsTableHeader = (
    <div className="table-header">
      {t('pins.header')}
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('common.search')}
        />
      </span>
    </div>
  );

  const bodyTemplate = (data: Group, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const connectorTypeTemplate = (data: Group, props: ColumnBodyOptions) => {
    return <>{connectorTypeNameMap[data[props.field]]}</>;
  };

  const statusTemplate = (data: PinStatusResponse, props: ColumnBodyOptions) => {
    const field = data[props.field];
    return <>{field ? t(`pins.statuses.${field}`) : ''}</>;
  };

  const setPin = async (data: PinStatusResponse, status: '0' | '1') => {
    const request = {
      groupId: data.groupId,
      connectorId: data.id,
      ioId: data.ioId,
      status: status,
    };
    try {
      await pinStatusService.setPin(request);
      showToast(data.groupTemplateConnectorName, t('pins.pin_toggled') + t(`pins.statuses.${status}`), 'success');
      loadData();
    } catch (error) {
      showToast(data.groupTemplateConnectorName, t('pins.pin_toggle_error'), 'error');
    }
  };

  const actionTemplate = (data: PinStatusResponse) => {
    const forceDisabled = data.groupType !== 'CUSTOM' && !canEditAll;
    return (
      <span>
        {data.groupTemplateConnectorType === 'OUTPUT' && (
          <Button
            type="button"
            label={t('pins.statuses.1')}
            className="p-button-success mr-2"
            disabled={forceDisabled || data.status !== '0'}
            onClick={() => {
              setPin(data, '1');
            }}
          ></Button>
        )}
        {data.groupTemplateConnectorType === 'OUTPUT' && (
          <Button
            type="button"
            label={t('pins.statuses.0')}
            className="p-button-danger mr-2"
            disabled={forceDisabled || data.status !== '1'}
            onClick={() => {
              setPin(data, '0');
            }}
          ></Button>
        )}
      </span>
    );
  };
  const rowHeaderTemplate = (data) => {
    return (
      <div className="flex align-items-center gap-2">
        <span className="font-bold">{data.groupName}</span>
      </div>
    );
  };

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={pins}
            paginator
            className="p-datatable-customers"
            rows={50}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            emptyMessage={t('pins.empty_message')}
            header={groupsTableHeader}
            groupRowsBy="groupName"
            rowGroupMode="subheader"
            rowGroupHeaderTemplate={rowHeaderTemplate}
          >
            <Column field="groupTemplateConnectorName" header={t('common.name')} sortable body={bodyTemplate}></Column>
            <Column
              field="groupTemplateConnectorType"
              header={t('common.type')}
              sortable
              body={connectorTypeTemplate}
            ></Column>
            <Column field="ioName" header={t('pins.io_name')} sortable body={bodyTemplate}></Column>
            <Column field="pin" header={t('pins.pin')} sortable body={bodyTemplate}></Column>
            <Column field="status" header={t('pins.status')} sortable body={statusTemplate}></Column>
            <Column
              headerStyle={{ width: '12rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
        </div>
      </div>

      <Toast ref={toast} />
    </div>
  );
};
